import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Brain,
  HeartPulse,
  Salad,
  Moon,
  Waves,
  Droplets,
  Sparkles,
} from "lucide-react";

// ---------------------------------------------------------------------------
// AI Health Twin
// A stylized human avatar surrounded by six "aura" nodes that reflect the
// user's Mind, Body, Nutrition, Sleep, Stress and Hydration scores.
// Designed to be an editorial, Awwwards-quality centerpiece on the score view.
// ---------------------------------------------------------------------------

const DIMENSIONS = [
  {
    key: "mind",
    label: "Mind",
    Icon: Brain,
    // (x, y) in the 400x520 SVG viewBox — points to the head
    node: { x: 200, y: 92 },
    // where the info card floats around the avatar
    side: "left",
    top: "8%",
  },
  {
    key: "sleep",
    label: "Sleep",
    Icon: Moon,
    node: { x: 148, y: 108 },
    side: "left",
    top: "28%",
  },
  {
    key: "stress",
    label: "Stress",
    Icon: Waves,
    node: { x: 200, y: 210 },
    side: "left",
    top: "50%",
  },
  {
    key: "hydration",
    label: "Hydration",
    Icon: Droplets,
    node: { x: 252, y: 160 },
    side: "right",
    top: "8%",
  },
  {
    key: "nutrition",
    label: "Nutrition",
    Icon: Salad,
    node: { x: 200, y: 288 },
    side: "right",
    top: "28%",
  },
  {
    key: "body",
    label: "Body",
    Icon: HeartPulse,
    node: { x: 200, y: 400 },
    side: "right",
    top: "50%",
  },
];

const statusFromScore = (s) => {
  if (s >= 80) return "Thriving";
  if (s >= 60) return "Balanced";
  if (s >= 40) return "Watch";
  return "Needs Care";
};

// Themed accent per score bracket. Kept in the SilentScan AI cyan/indigo family
// so it stays cohesive with the rest of the product.
const accentFor = (score) => {
  if (score >= 80) return { hex: "#34d399", ring: "rgba(52,211,153,0.7)", glow: "rgba(52,211,153,0.35)" };
  if (score >= 60) return { hex: "#22d3ee", ring: "rgba(34,211,238,0.7)", glow: "rgba(34,211,238,0.32)" };
  if (score >= 40) return { hex: "#fbbf24", ring: "rgba(251,191,36,0.7)", glow: "rgba(251,191,36,0.32)" };
  return { hex: "#f87171", ring: "rgba(248,113,113,0.7)", glow: "rgba(248,113,113,0.32)" };
};

const HealthTwin = ({ twinScores, twinInsights, overallScore = 0 }) => {
  const scores = twinScores || {};
  const insights = useMemo(() => {
    const map = {};
    (twinInsights || []).forEach((it) => {
      if (it && it.dimension) map[it.dimension] = it;
    });
    return map;
  }, [twinInsights]);

  const overallAccent = accentFor(overallScore || 60);

  // container variants for staggered reveal
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.15 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <motion.div
      data-testid="health-twin"
      initial="hidden"
      animate="show"
      variants={container}
      className="relative mt-10 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent"
    >
      {/* section header */}
      <div className="relative z-10 flex flex-wrap items-end justify-between gap-4 px-6 pt-6 sm:px-9 sm:pt-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-300/80">
            Chapter 03 · Living Portrait
          </div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold text-white leading-tight">
            Your AI Health Twin
          </h3>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed max-w-md">
            Not just a score — a living visualization of six dimensions of
            wellbeing, drawn from your answers.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass-card border border-white/10 text-[11px] uppercase tracking-widest text-cyan-300">
          <Sparkles className="w-3 h-3" />
          Explainable · Live
        </div>
      </div>

      {/* Ambient grain / radial glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background: `radial-gradient(60% 55% at 50% 55%, ${overallAccent.glow} 0%, transparent 70%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* main grid */}
      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6 px-4 sm:px-8 pb-8 pt-4">
        {/* Avatar column */}
        <div className="relative mx-auto w-full max-w-[520px]">
          <TwinAvatar dimensions={DIMENSIONS} scores={scores} />
        </div>

        {/* Stats column */}
        <motion.div variants={item} className="flex flex-col gap-3">
          {DIMENSIONS.map((d) => {
            const value = Number(scores[d.key] ?? 0);
            const ins = insights[d.key];
            const status = ins?.status || statusFromScore(value);
            const note = ins?.note || "";
            return (
              <TwinStatCard
                key={d.key}
                Icon={d.Icon}
                label={d.label}
                score={value}
                status={status}
                note={note}
                testId={`twin-stat-${d.key}`}
              />
            );
          })}
        </motion.div>
      </div>

      {/* footer disclosure */}
      <div className="relative z-10 border-t border-white/5 px-6 py-4 sm:px-9 text-[11px] text-zinc-500 leading-relaxed">
        Your Health Twin updates every time you re-take the check-in. It reflects
        preventive awareness — not a medical diagnosis.
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Avatar with 6 pulsing anchor nodes
// ---------------------------------------------------------------------------
const TwinAvatar = ({ dimensions, scores }) => {
  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Concentric rings behind avatar */}
      <svg
        viewBox="0 0 400 500"
        className="absolute inset-0 w-full h-full"
        aria-hidden
      >
        <defs>
          <radialGradient id="twinCore" cx="50%" cy="45%" r="55%">
            <stop offset="0%" stopColor="rgba(34,211,238,0.35)" />
            <stop offset="45%" stopColor="rgba(59,130,246,0.15)" />
            <stop offset="100%" stopColor="rgba(2,6,23,0)" />
          </radialGradient>
          <linearGradient id="silhouette" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(178,231,255,0.98)" />
            <stop offset="60%" stopColor="rgba(103,182,255,0.85)" />
            <stop offset="100%" stopColor="rgba(59,130,246,0.35)" />
          </linearGradient>
          <filter id="softGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Core glow behind avatar */}
        <motion.circle
          cx="200"
          cy="240"
          r="180"
          fill="url(#twinCore)"
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Slow rotating faint ring */}
        <motion.g
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 60, ease: "linear", repeat: Infinity }}
          style={{ originX: "200px", originY: "240px" }}
        >
          <circle
            cx="200"
            cy="240"
            r="180"
            fill="none"
            stroke="rgba(34,211,238,0.14)"
            strokeWidth="1"
            strokeDasharray="2 8"
          />
          <circle
            cx="200"
            cy="240"
            r="140"
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
          />
        </motion.g>

        {/* Human silhouette (front view, stylized) */}
        <motion.path
          d="
            M200 60
            c-24 0 -42 20 -42 44
            c0 20 12 36 26 42
            c-2 6 -4 10 -8 14
            c-24 8 -46 22 -56 44
            c-6 14 -8 34 -8 58
            c0 26 4 46 12 62
            c-4 24 -6 44 -8 66
            l6 108
            c1 12 10 20 22 20
            c10 0 18 -6 20 -18
            l10 -96
            l4 -60
            l4 60
            l10 96
            c2 12 10 18 20 18
            c12 0 21 -8 22 -20
            l6 -108
            c-2 -22 -4 -42 -8 -66
            c8 -16 12 -36 12 -62
            c0 -24 -2 -44 -8 -58
            c-10 -22 -32 -36 -56 -44
            c-4 -4 -6 -8 -8 -14
            c14 -6 26 -22 26 -42
            c0 -24 -18 -44 -42 -44
            z"
          fill="url(#silhouette)"
          fillOpacity="0.16"
          stroke="url(#silhouette)"
          strokeWidth="1.3"
          filter="url(#softGlow)"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Connective lines from each anchor to the center */}
        {dimensions.map((d) => {
          const score = Number(scores[d.key] ?? 0);
          const accent = accentFor(score);
          return (
            <motion.line
              key={`line-${d.key}`}
              x1={d.node.x}
              y1={d.node.y}
              x2="200"
              y2="240"
              stroke={accent.ring}
              strokeOpacity="0.35"
              strokeWidth="0.8"
              strokeDasharray="2 4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.9 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          );
        })}

        {/* Anchor nodes with pulse */}
        {dimensions.map((d, i) => {
          const score = Number(scores[d.key] ?? 0);
          const accent = accentFor(score);
          return (
            <g key={`node-${d.key}`}>
              <motion.circle
                cx={d.node.x}
                cy={d.node.y}
                r="16"
                fill={accent.glow}
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{
                  opacity: [0.15, 0.55, 0.15],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 3.2,
                  repeat: Infinity,
                  delay: 0.3 + i * 0.12,
                }}
              />
              <motion.circle
                cx={d.node.x}
                cy={d.node.y}
                r="5"
                fill={accent.hex}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.4 + i * 0.1 }}
              />
              <motion.text
                x={d.node.x}
                y={d.node.y - 22}
                textAnchor="middle"
                fill="rgba(255,255,255,0.85)"
                style={{
                  fontFamily: "ui-sans-serif, system-ui, -apple-system",
                  fontSize: "10px",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                }}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + i * 0.1 }}
              >
                {d.label}
              </motion.text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stat card with animated progress ring
// ---------------------------------------------------------------------------
const TwinStatCard = ({ Icon, label, score, status, note, testId }) => {
  const accent = accentFor(score);
  const size = 52;
  const stroke = 4;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, score)) / 100) * c;

  const item = {
    hidden: { opacity: 0, x: 12 },
    show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <motion.div
      data-testid={testId}
      variants={item}
      whileHover={{ y: -2, transition: { duration: 0.2 } }}
      className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-4 backdrop-blur-md hover:bg-white/[0.06] transition-colors"
    >
      <div className="flex items-center gap-4">
        <div className="relative flex-shrink-0">
          <svg width={size} height={size} className="-rotate-90">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={stroke}
              fill="none"
            />
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              stroke={accent.hex}
              strokeWidth={stroke}
              fill="none"
              strokeLinecap="round"
              strokeDasharray={c}
              initial={{ strokeDashoffset: c }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
              style={{ filter: `drop-shadow(0 0 6px ${accent.glow})` }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="w-4 h-4" style={{ color: accent.hex }} />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-sm font-medium text-white">{label}</div>
            <div
              className="text-lg font-display font-semibold tabular-nums"
              style={{ color: accent.hex }}
            >
              {Math.round(score)}
              <span className="text-[10px] text-zinc-500 ml-0.5">/100</span>
            </div>
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: accent.hex }}
            />
            <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
              {status}
            </span>
          </div>
          {note && (
            <p className="mt-1.5 text-[11px] text-zinc-400 leading-snug line-clamp-2">
              {note}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default HealthTwin;
